import React from 'react';
import styled from 'styled-components';

import { Box } from 'design/atoms/Box';
import { STFlex } from 'design/atoms/Container';
import { BaseText, STH1 } from 'design/atoms/Text';
import { STButton } from 'design/atoms/Button';
import { StyledLink } from 'src/shared/StyledLink';
import airplanePNG from 'src/static/images/air-plane.png';
import policyPNG from 'src/static/images/policy.png';
import RangeSlider from 'src/components/RangeSlider';
import { FaqsAccordion } from 'src/shared/FaqsAccordion';
import content from 'src/content/home.json';
import faqContent from 'src/content/faqs.json';
import { SubFooter } from 'src/shared/SubFooter';
import { analytics } from 'src/utils/firebase';

const InformationCard = styled(STFlex)`
  margin-bottom: 3rem;
  &:hover {
    background: #fff;
    box-shadow: 0 3px 10px 1px rgba(0, 0, 0, 0.05);
  }
`;

const StyledHeaderImg = styled.img`
  object-fit: cover;
  width: 100%;
  height: 394px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    width: 45%;
    height: 95%;
    border-bottom-right-radius: 1.5rem;
    border-top-left-radius: 1.5rem;
    margin: 1rem;
  }
`;

const StyledPolicyImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 50%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    width: 32.5%;
    height: auto;
  }
`;

const Home: React.FC = () => {
  const [rangeValue, setRangeValue] = React.useState(2);
  const getRangeValue = value => {
    setRangeValue(value[0]);
  };

  React.useEffect(() => {
    analytics.logEvent('screen_view', {
      screen: 'home',
    });
  }, []);

  const renderAboutInformationBoxes = contentList => {
    return contentList.map((item, index) => {
      return (
        <InformationCard
          key={index}
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
          mx={[6, 0]}
          py={[3, 5]}
          px={[1, 3]}
          my={[1, 0]}
          width={330}
          borderRadius={3}
          minHeight={310}
        >
          <Box
            as="p"
            fontSize={30}
            backgroundColor="#F5AC30"
            color="white"
            borderRadius={50}
            textAlign="center"
            pt={14}
            width={64}
            height={64}
            fontFamily="sourceSerifPro"
          >
            {index + 1}
          </Box>
          <BaseText
            variant="blueSemiBold"
            fontSize={24}
            fontFamily="montserrat"
            my={3}
          >
            {item.heading}
          </BaseText>
          <BaseText
            textAlign="center"
            variant="greyRegular"
            fontSize={16}
            fontFamily="montserrat"
            width={'100%'}
          >
            <span dangerouslySetInnerHTML={{ __html: item.content }} />
          </BaseText>
        </InformationCard>
      );
    });
  };

  const renderHeaderSection = () => {
    return (
      <Box as="section" mt={64} mb={[60, 0]}>
        <STFlex
          flexDirection={['column-reverse', 'row']}
          justifyContent="space-between"
          bg={['white', '#F2F8FE']}
          width={['100%']}
          height={600}
        >
          <STFlex
            flexDirection="column"
            justifyContent={['center', 'flex-start']}
            alignItems={['center', 'stretch']}
            borderBottomLeftRadius={40}
            pl={[10, 80]}
            pr={[10, 120]}
            pt={[70, 140]}
            ml={[0, '5%']}
            bg={['white', '#F2F8FE']}
          >
            <STH1
              variant="blueSemiBold"
              fontSize={[24, 46]}
              fontFamily="montserrat"
              width={[340, '100%']}
              textAlign={['center', 'left']}
            >
              {content.header.primaryHeading}
            </STH1>
            <BaseText
              variant="lightGrayMedium"
              my={4}
              fontSize={[16, 20]}
              fontFamily="montserrat"
              width={[340, '100%']}
              textAlign={['center', 'left']}
            >
              {content.header.secondaryHeading}
            </BaseText>
            <StyledLink
              onClick={() => {
                analytics.logEvent('button_click', {
                  name: 'Hero button',
                });
              }}
              to="/join"
              width={250}
            >
              <STButton height={52} fontWeight={600}>
                {content.header.button}
              </STButton>
            </StyledLink>
          </STFlex>
          <StyledHeaderImg src={airplanePNG} />
        </STFlex>
      </Box>
    );
  };

  const renderEarningEstimationSection = () => {
    return (
      <STFlex
        flexDirection={['column', 'row']}
        width={['100%', '60%']}
        justifyContent={['center', 'space-between']}
        alignSelf="center"
        alignItems="center"
        position={['static', 'relative']}
        bg="white"
        bottom={[null, 50]}
        borderRadius={4}
        py={3}
        px={4}
        boxShadow="0 7px 7px lightgrey"
        mt={[3, 0]}
      >
        <STFlex flexDirection="column" width={['100%', '61%']}>
          <STH1
            color="#636363"
            fontFamily="montserrat"
            fontSize={16}
            fontWeight={700}
            textAlign={['center', 'left']}
          >
            {content.earningEstimation.prompt}
          </STH1>
          <RangeSlider rangeValue={getRangeValue} />
        </STFlex>
        <STFlex flexDirection="column" justifyContent="center" mt={[4, 0]}>
          <STH1
            color="#000"
            fontFamily="montserrat"
            fontSize={32}
            fontWeight={600}
            textAlign="center"
          >
            {rangeValue > 1 ? (
              rangeValue === 7 ? (
                'PKR 100,000+'
              ) : (
                `PKR ${((rangeValue - 1) * 20000).toLocaleString()}`
              )
            ) : (
              <BaseText
                color="#000"
                fontFamily="montserrat"
                fontSize={20}
                textAlign="center"
              >
                {content.earningEstimation.getStarted}
              </BaseText>
            )}
          </STH1>
          <STH1
            color="#707070"
            fontFamily="montserrat"
            fontSize={14}
            fontWeight={600}
            textAlign="center"
          >
            {rangeValue > 1 && content.earningEstimation.perMonth}
          </STH1>
        </STFlex>
      </STFlex>
    );
  };

  const renderPolicySection = () => {
    return (
      <Box as="section">
        <STFlex
          justifyContent="center"
          bg="white"
          width="100%"
          px={[0, 7]}
          py={[4, 6]}
        >
          <STFlex
            width={['100%', '90%']}
            flexDirection={['column', 'row']}
            justifyContent={['center', 'space-between']}
            alignItems="center"
            bg={['#fff', '#F2F8FE']}
            borderRadius={3}
            p={4}
          >
            <StyledPolicyImage src={policyPNG} />
            <STFlex
              flexDirection="column"
              justifyContent={['center', 'flex-start']}
              alignItems={['center', 'stretch']}
              px={[10, 0]}
              my={[4, 0]}
              width={['100%', '60%']}
            >
              <STH1
                variant="blackMedium"
                fontSize={[24, 36]}
                fontFamily="montserrat"
                width={[320, '100%']}
                textAlign={['center', 'left']}
                my={[2, 0]}
              >
                {content.policy.primaryHeading}
              </STH1>
              <BaseText
                variant="blueRegular"
                mt={3}
                mb={5}
                fontSize={[16, 22]}
                fontFamily="montserrat"
                width={[340, '75%']}
                textAlign={['center', 'left']}
                py={[2, 2]}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: content.policy.secondaryHeading,
                  }}
                />
              </BaseText>
              <StyledLink
                onClick={() => {
                  analytics.logEvent('button_click', {
                    name: 'Policy button',
                  });
                }}
                to="/join"
              >
                <STButton width={[250]} height={52} fontWeight={600}>
                  {content.policy.button}
                </STButton>
              </StyledLink>
            </STFlex>
          </STFlex>
        </STFlex>
      </Box>
    );
  };

  const renderHowItWorksSection = () => {
    return (
      <Box
        as="section"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        pb={6}
        bg={['#fff', '#F2F8FE']}
      >
        <BaseText
          fontFamily="montserrat"
          variant="blackMedium"
          fontSize={40}
          my={[4, 6]}
        >
          {content.working.primaryHeading}
        </BaseText>
        <STFlex
          width={'80%'}
          flexDirection={['column', 'row']}
          justifyContent="space-around"
          alignItems="center"
        >
          {renderAboutInformationBoxes(content.working.steps)}
        </STFlex>
      </Box>
    );
  };

  const renderFAQ = () => {
    return (
      <Box
        as="section"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        pb={4}
      >
        <BaseText
          fontFamily="montserrat"
          variant="blackMedium"
          fontSize={[24, 40]}
          my={[4, 6]}
          textAlign="center"
          width={['320px', '100%']}
        >
          Frequently asked questions
        </BaseText>
        <FaqsAccordion data={faqContent.faqs.slice(0, 4)} />
        <BaseText
          fontSize={[14, 16]}
          mt={20}
          color="#484848"
          fontFamily="montserrat"
        >
          {content.faq.question}{' '}
          <StyledLink to="/faqs" color="#006FAE">
            {content.faq.seeMore}
          </StyledLink>
        </BaseText>
      </Box>
    );
  };

  const renderSubFooter = () => {
    return <SubFooter />;
  };

  return (
    <STFlex flexDirection="column" bg="white">
      {renderHeaderSection()}
      {renderEarningEstimationSection()}
      {renderPolicySection()}
      {renderHowItWorksSection()}
      {renderFAQ()}
      {renderSubFooter()}
    </STFlex>
  );
};

export default Home;
